import { useEffect, useRef, useState } from 'react';
import { MoreVertical } from 'lucide-react';
import { useTransactionStore } from '@/store/transactionsStore';
import { useAuthStore } from '@/store/authStore';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import EditTransaction from './EditTransaction';
import { useTranslation } from 'react-i18next';
import { Transaction } from '@flows.cash/types';

interface TransactionActionsProps {
  transactionId: string;
  onEdit?: () => void;
}

export default function TransactionActions({ transactionId, onEdit }: TransactionActionsProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const { user } = useAuthStore();
  
  const transactions = useTransactionStore(state => state.transactions);
  const deleteTransaction = useTransactionStore(state => state.deleteTransaction);
  const getFilteredTransactions = useTransactionStore(state => state.getFilteredTransactions);
  
  const transaction = transactions?.find((t) => t.id === transactionId);
  const { t: translate } = useTranslation();

  const filteredTransactions = getFilteredTransactions();

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDelete = async () => {
    if (!user?.id) {
      toast.error("You must be logged in to delete transactions");
      return;
    }
    
    if (!transaction) {
      toast.error("Transaction not found");
      return;
    }

    try {
      await deleteTransaction(transactionId);
      setIsOpen(false);
      setShowDeleteAlert(false);
    } catch (error) {
      console.error('Error in handleDelete:', error);
      toast.error("Failed to delete transaction");
    }
  };

  return (
    <>
      <div ref={menuRef} className="relative">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <MoreVertical className="h-4 w-4 text-gray-500" />
        </button>

        {isOpen && (
          <div className="absolute right-0 mt-1 w-48 rounded-md shadow-lg bg-white dark:bg-gray-800 ring-1 ring-black ring-opacity-5 z-50">
            <div className="py-1">
              <button
                onClick={() => {
                  setShowEditModal(true);
                  setIsOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-white bg-gradient-to-r from-[#1E3A8A] to-[#2563EB] hover:from-[#1E3A8A]/90 hover:to-[#2563EB]/90 rounded-lg"
              >
                {translate('actions.edit')}
              </button>
              <button
                onClick={() => {
                  setShowDeleteAlert(true);
                  setIsOpen(false);
                }}
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/10 rounded-lg"
              >
                {translate('actions.delete')}
              </button>
            </div>
          </div>
        )}
      </div>

      {showEditModal && transaction && (
        <EditTransaction
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            onEdit?.();
          }}
          // @ts-ignore - Type mismatch during migration
          currentTransaction={transaction}
        />
      )}

      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{translate('dialogs.deleteTransaction.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {translate('dialogs.deleteTransaction.description')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{translate('actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600"
              onClick={handleDelete}
            >
              {translate('actions.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}